import { useAuthSignInWithEmailAndPassword, useAuthSignOut } from "@react-query-firebase/auth"
import { Button, Col, Divider, Form, Input, Modal, message, PageHeader, Row, Spin, Typography } from "antd"
import CountryPhoneInput, { ConfigProvider } from "antd-country-phone-input"
import {
  getMultiFactorResolver,
  getRedirectResult,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth"
import { useEffect, useReducer, useRef, useState } from "react"
import { useQueryClient } from "react-query"
import { Link, useHistory } from "react-router-dom"
import en from "world_countries_lists/data/countries/en/world.json"
import { CustomEmailPassword } from "../../components/auth/CustomEmailPassword"
import { EmailPassword } from "../../components/auth/EmailPassword"
import { Microsoft } from "../../components/auth/Microsoft"
import { SupportModal } from "../../components/SupportModal"
import { auth } from "../../firebase"
import { useHandleTenantId } from "../../hooks/useHandleTenantId"
import { useQueryParam } from "../../hooks/useQueryParam"
import { useLoggerMutation } from "../../network/useLogger"
import {
  usePasswordResetConfirmMutation,
  usePasswordResetRequestMutation,
  usePreAuthMutation,
} from "../../network/usePerson"

function signInReducer(state, action) {
  const { type, payload, field } = action
  switch (type) {
    case "field": {
      return {
        ...state,
        [field]: payload,
      }
    }
    default: {
      throw new Error(`Unsupported action type: ${type}`)
    }
  }
}

function getPasswordResetResponseMessage(data) {
  if (data == null) {
    return null
  }
  if (typeof data === "string") {
    return data
  }
  const nested = data.data
  if (typeof nested === "string") {
    return nested
  }
  if (nested && typeof nested === "object") {
    return nested.message ?? nested.Message ?? null
  }
  return data.message ?? data.Message ?? null
}

function normalizePasswordResetMessage(message) {
  return (message ?? "").trim().toLowerCase()
}

/** True when reset-request succeeded and the user should enter a verification code. */
function isPasswordResetRequestSent(message) {
  return normalizePasswordResetMessage(message).includes("if account exists")
}

/** True when the account cannot be reset through this flow (e.g. no email/SMS on file). */
function isPasswordResetRequestBlocked(message) {
  return normalizePasswordResetMessage(message).includes("no contact method")
}

/** Builds the `identifier` string for backend password reset (plain employee / ID, E.164 mobile, email). */
function buildPasswordResetIdentifier(signInType, raw) {
  switch (signInType) {
    case "mobile": {
      if (!raw || typeof raw !== "object") {
        return ""
      }
      const phone = raw.phone == null ? "" : String(raw.phone).replace(/\s+/g, "")
      if (!phone) {
        return ""
      }
      const code = raw.code == null ? "" : String(raw.code).replace(/\D/g, "")
      return `+${code}${phone}`
    }
    case "employeeNumber":
    case "identificationNumber":
      return raw == null ? "" : String(raw).trim()
    case "email":
    default:
      return raw == null ? "" : String(raw).trim()
  }
}

export function SignInWrapper() {
  const queryParam = useQueryParam()
  const SIGN_IN_STATE = {
    type: null,
    tenantId: queryParam.get("tenantId") || process.env.REACT_APP_TENANT_ID,
    reset: false,
    loading: false,
    authenticated: false,
    authorized: false,
    returnUrl: queryParam.get("returnUrl"),
    isAdmin: queryParam.get("isAdmin"),
    adminUrl: queryParam.get("adminUrl"),
    mfa: false,
    resolver: false,
    verificationId: false,
    support: false,
  }

  const [state, dispatch] = useReducer(signInReducer, SIGN_IN_STATE)
  const [passwordResetStep, setPasswordResetStep] = useState("request")
  const [passwordResetIdentifier, setPasswordResetIdentifier] = useState("")
  const DEFAULT_BG =
    "https://images.unsplash.com/photo-1612999805994-1eabaef7bb88?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1650&q=80"
  const { tenant, isLoading, isError, awaitTenant } = useHandleTenantId(state, dispatch)
  const buttonRef = useRef(null)
  const history = useHistory()

  const queryClient = useQueryClient()

  auth.tenantId = state.tenantId || process.env.REACT_APP_TENANT_ID
  const { mutate: signOutMutation } = useAuthSignOut(auth)
  const passwordResetRequestMutation = usePasswordResetRequestMutation()
  const passwordResetConfirmMutation = usePasswordResetConfirmMutation()
  const emailMutation = useAuthSignInWithEmailAndPassword(auth)
  const externalAuthPreMutation = usePreAuthMutation()
  const logMutation = useLoggerMutation()

  useEffect(() => {
    queryClient.invalidateQueries()
  }, [])

  const getReturnUrl = () => {
    let path =
      queryParam.get("returnUrl") && !queryParam.get("returnUrl").includes("auth") ? queryParam.get("returnUrl") : "/"
    if (state.isAdmin && queryParam.get("returnUrl")) {
      const params = new URLSearchParams({
        isAdmin: state?.isAdmin,
      }).toString()
      path = path + `${params.length ? `?${params}` : ""}`
    }
    return path
  }

  const handleEmailAuthentication = async (payload) => {
    try {
      const res = await emailMutation.mutateAsync(payload)
      window.localStorage.setItem("tenantId", res?.user?.tenantId)
      logMutation.mutate({
        data: {
          event: "login",
          context: {
            type: state?.type,
          },
        },
      })
      window.location.pathname = getReturnUrl()
    } catch (error) {
      if (error.code === "auth/multi-factor-auth-required") {
        const fnResolver = getMultiFactorResolver(auth, error)
        dispatch({ type: "field", field: "resolver", payload: fnResolver })
        if (fnResolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
          const phoneInfoOptions = {
            multiFactorHint: fnResolver.hints[0],
            session: fnResolver.session,
          }
          const recaptchaVerifier = new RecaptchaVerifier(buttonRef.current, { size: "invisible" }, auth)
          const verificationId = await new PhoneAuthProvider(auth).verifyPhoneNumber(
            phoneInfoOptions,
            recaptchaVerifier
          )
          dispatch({
            type: "field",
            field: "verificationId",
            payload: verificationId,
          })
          dispatch({ type: "field", field: "mfa", payload: true })
          window.localStorage.setItem("tenantId", tenant?.tenantId)
        }
      } else {
        console.error(error)
        message.error(`Error Authenticating: ${error.message}`)
        dispatch({ type: "field", field: "loading", payload: false })
      }
    }
  }

  getRedirectResult(auth).then(async (res) => {
    if (res) {
      dispatch({ type: "field", field: "loading", payload: true })
      try {
        await externalAuthPreMutation.mutateAsync({
          data: {
            email: res.user.email,
            authUid: res.user.uid,
            microsoftProfile: res,
          },
        })
        logMutation.mutate({
          data: {
            event: "login",
            context: {
              type: "sso",
            },
          },
        })

        window.location.pathname = getReturnUrl()
      } catch (error) {
        message.error("Failed to sign in using credentials provided")
        dispatch({ type: "field", field: "loading", payload: false })
        window.localStorage.clear()
        window.sessionStorage.clear()
        await signOutMutation()
      }
    }
  })

  const verifyCode = async (code) => {
    const cred = PhoneAuthProvider.credential(state?.verificationId, code)
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
    await state?.resolver.resolveSignIn(multiFactorAssertion)
  }

  const emailModifier = ({ prefix = null, suffix = null, email }) => {
    let modifiedEmail = email
    if (prefix) {
      modifiedEmail = prefix + modifiedEmail
    }
    if (suffix) {
      modifiedEmail += suffix
    }
    return modifiedEmail
  }

  const description = (
    <Link to={`/auth/register?tenantId=${tenant?.tenantId}`}>
      <Typography.Link style={{ color: "white", textDecoration: "underline" }}>Link existing account.</Typography.Link>
    </Link>
  )
  const selfReg = (
    <Link to={`/auth/self-register?tenantId=${tenant?.tenantId}`}>
      <Typography.Link style={{ color: "white", textDecoration: "underline" }}>Register Now.</Typography.Link>
    </Link>
  )

  const passwordResetTenantId = state.tenantId || tenant?.tenantId || auth.tenantId

  const resetIdentifierLabel =
    state?.type === "employeeNumber"
      ? "Employee number"
      : state?.type === "identificationNumber"
      ? "Identification number"
      : state?.type === "mobile"
      ? "Mobile number"
      : "Email address"

  const resetIdentifierPlaceholder =
    state?.type === "employeeNumber"
      ? "Employee number..."
      : state?.type === "identificationNumber"
      ? "Identification number..."
      : state?.type === "mobile"
      ? "Mobile number..."
      : "Email address..."

  const closePasswordResetModal = () => {
    dispatch({ type: "field", field: "reset", payload: false })
    setPasswordResetStep("request")
    setPasswordResetIdentifier("")
  }

  return (
    <>
      <SupportModal
        setVisible={() => dispatch({ type: "field", field: "support", payload: false })}
        support={tenant?.support}
        visible={state?.support}
      />
      <Modal
        cancelButtonProps={{ style: { display: "none" } }}
        className="modal"
        closable={false}
        okButtonProps={{ style: { display: "none" } }}
        visible={state?.mfa}
      >
        <PageHeader
          className="site-page-header"
          onBack={() => {
            dispatch({ type: "field", field: "mfa", payload: false })
            dispatch({ type: "field", field: "loading", payload: false })
          }}
          title="Confirm your number"
        />
        <Form
          layout="vertical"
          onFinish={async (values) => {
            await verifyCode(values.code)
            window.location.pathname = getReturnUrl()
          }}
        >
          <Form.Item label="Enter the code we sent over SMS" name="code">
            <Input />
          </Form.Item>
          <Button htmlType="submit" type="primary">
            Enter Code
          </Button>
        </Form>
      </Modal>
      <Modal
        cancelButtonProps={{ style: { display: "none" } }}
        className="modal"
        destroyOnClose
        okButtonProps={{ style: { display: "none" } }}
        onCancel={closePasswordResetModal}
        visible={state?.reset}
      >
        <Typography.Title level={3}>Reset password</Typography.Title>
        <Divider />
        {passwordResetStep === "request" ? (
          <Form
            initialValues={state?.type === "mobile" ? { identifier: { short: "za" } } : undefined}
            layout="vertical"
            name="password-reset-request"
            onFinish={async (values) => {
              const identifier = buildPasswordResetIdentifier(state?.type, values.identifier)
              if (!identifier) {
                message.warning("Please enter your account identifier.")
                return
              }
              const data = await passwordResetRequestMutation.mutateAsync({
                tenantId: passwordResetTenantId,
                identifier,
                provider: state?.type,
              })
              const msg = getPasswordResetResponseMessage(data)
              if (isPasswordResetRequestBlocked(msg)) {
                message.warning(msg)
                return
              }
              if (isPasswordResetRequestSent(msg)) {
                message.info(msg)
                setPasswordResetIdentifier(identifier)
                setPasswordResetStep("confirm")
                return
              }
              if (msg) {
                message.info(msg)
              }
            }}
          >
            <Typography.Paragraph>
              Enter the same identifier you use to sign in. We will send you a verification code by email or SMS when
              notifications are enabled for your organisation.
            </Typography.Paragraph>
            {state?.type === "mobile" ? (
              <ConfigProvider locale={en}>
                <Form.Item
                  label={resetIdentifierLabel}
                  name="identifier"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (buildPasswordResetIdentifier("mobile", value)) {
                          return Promise.resolve()
                        }
                        return Promise.reject(new Error("Please enter your mobile number"))
                      },
                    },
                  ]}
                >
                  <CountryPhoneInput placeholder={resetIdentifierPlaceholder} size="small" style={{ width: "100%" }} />
                </Form.Item>
              </ConfigProvider>
            ) : (
              <Form.Item
                label={resetIdentifierLabel}
                name="identifier"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Input placeholder={resetIdentifierPlaceholder} type={state?.type === "email" ? "email" : "text"} />
              </Form.Item>
            )}
            <Button block htmlType="submit" loading={passwordResetRequestMutation.isLoading}>
              Send verification code
            </Button>
          </Form>
        ) : (
          <Form
            layout="vertical"
            name="password-reset-confirm"
            onFinish={async (values) => {
              const data = await passwordResetConfirmMutation.mutateAsync({
                tenantId: passwordResetTenantId,
                identifier: passwordResetIdentifier,
                code: values.code,
                newPassword: values.newPassword,
              })
              const msg = getPasswordResetResponseMessage(data)
              message.success(msg || "Your password has been reset. You can sign in now.")
              closePasswordResetModal()
            }}
          >
            <Typography.Paragraph>
              Enter the verification code we sent, then choose a new password.
            </Typography.Paragraph>
            <Form.Item label="Verification code" name="code" rules={[{ required: true, message: "Code is required" }]}>
              <Input autoComplete="one-time-code" />
            </Form.Item>
            <Form.Item
              hasFeedback
              label="New password"
              name="newPassword"
              rules={[{ required: true, message: "Please enter a new password" }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              dependencies={["newPassword"]}
              hasFeedback
              label="Confirm new password"
              name="confirmPassword"
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error("The two passwords do not match"))
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button block htmlType="submit" loading={passwordResetConfirmMutation.isLoading} type="primary">
              Reset password
            </Button>
            <Button
              block
              onClick={() => {
                setPasswordResetStep("request")
                setPasswordResetIdentifier("")
              }}
              type="link"
            >
              Use a different identifier
            </Button>
          </Form>
        )}
      </Modal>
      <div
        className="auth"
        style={{
          backgroundImage: `url(${tenant?.featureImageUrl || DEFAULT_BG})`,
        }}
      >
        <Row>
          <Col
            className="auth__pannel"
            lg={10}
            md={12}
            style={
              state.tenantId === "OLA-Energy-epdiv"
                ? {
                    background: "rgba(255, 255, 255, 0.5)",
                    backdropFilter: "blur(10px)",
                  }
                : {}
            }
            xl={8}
            xs={24}
          >
            <Form
              initialValues={{ remember: true }}
              layout="vertical"
              name="login"
              onFinish={async (values) => {
                dispatch({ type: "field", field: "loading", payload: true })
                const payload = {
                  email: values[state?.type] || values["email"] || values.identifier,
                  password: values.password,
                }
                switch (state?.type) {
                  case "identificationNumber": {
                    payload.email = emailModifier({
                      suffix: tenant?.providerConfig?.identificationNumber?.suffix,
                      email: payload?.email,
                    })
                    break
                  }
                  case "employeeNumber": {
                    payload.email = emailModifier({
                      suffix: tenant?.providerConfig?.employeeNumber?.suffix,
                      email: payload?.email,
                    })
                    break
                  }
                  case "mobile": {
                    payload.email = emailModifier({
                      prefix: `+${payload.email?.code}`,
                      suffix: tenant?.providerConfig?.mobile?.suffix,
                      email: payload.email.phone,
                    })
                    break
                  }
                }
                await handleEmailAuthentication(payload)
              }}
            >
              {awaitTenant || isLoading || isError ? (
                awaitTenant && isLoading ? (
                  <Spin tip="Fetching your authentication options" />
                ) : (
                  awaitTenant &&
                  isError && (
                    <>
                      <Typography.Text style={{ color: "white", marginBottom: "2rem" }}>
                        We are unable to fetch you authentication options
                      </Typography.Text>
                      <a href={`/auth/sign-in?${state?.tenantId ? `?tenantId=${state?.tenantId}` : ""}`}>
                        <Button block type="primary">
                          Please refresh
                        </Button>
                      </a>
                    </>
                  )
                )
              ) : (
                <>
                  {tenant?.imageUrl && <img alt="company" className="companyLogo" src={tenant?.imageUrl} />}
                  {(() => {
                    switch (state?.type) {
                      case "email":
                        return (
                          <EmailPassword
                            description={
                              <>
                                Sign in with your email and password. <br />
                                {!isLoading && tenant?.linkRegistration && description}
                                <br />
                                {!isLoading && tenant?.selfRegistrationEnabled && selfReg}
                              </>
                            }
                          />
                        )
                      case "employeeNumber":
                        return (
                          <CustomEmailPassword
                            description={
                              <>
                                Sign in with your employee number and password. <br />
                                {!isLoading && tenant?.linkRegistration && description}
                                <br />
                                {!isLoading && tenant?.selfRegistrationEnabled && selfReg}
                              </>
                            }
                            emailPlaceholder="Employee Number..."
                            title="Sign In"
                            type="text"
                          />
                        )
                      case "mobile":
                        return (
                          <CustomEmailPassword
                            description={
                              <>
                                Sign in with your mobile number. <br />
                                {!isLoading && tenant?.linkRegistration && description}
                                <br />
                                {!isLoading && tenant?.selfRegistrationEnabled && selfReg}
                              </>
                            }
                            emailPlaceholder="Mobile number..."
                            title="Sign In"
                            type="number"
                          />
                        )
                      case "microsoft":
                        return (
                          <Microsoft
                            auth={auth}
                            customText={tenant?.providerConfig?.[state.type]?.title}
                            description={
                              <>
                                Sign in with your work account using Single Sign on. <br />
                                {!(isLoading || state.tenantId.includes("Seriti")) &&
                                  tenant?.linkRegistration &&
                                  description}
                                <br />
                                {!isLoading && tenant?.selfRegistrationEnabled && selfReg}
                              </>
                            }
                            dispatch={dispatch}
                            providers={tenant?.OAuthProvider}
                            state={state}
                            tenant={tenant?.microsoftTenant}
                          />
                        )
                      case "identificationNumber":
                        return (
                          <EmailPassword
                            description={<>Sign in with your identification number {description}</>}
                            emailError="please enter a valid identification number"
                            emailPlaceholder="Identification number..."
                            title={tenant?.providerConfig?.[state.type]?.title ?? "Sign in with identification number"}
                            type={"text"}
                          />
                        )
                      default:
                        return (
                          <EmailPassword
                            description={
                              <>
                                Sign in with your email and password. <br />
                                {!isLoading && tenant?.linkRegistration && description}
                                <br />
                                {!isLoading && tenant?.selfRegistrationEnabled && selfReg}
                              </>
                            }
                          />
                        )
                    }
                  })()}
                  {state?.type !== "microsoft" && (
                    <Button
                      block
                      htmlType="submit"
                      id="recaptcha-container"
                      loading={state?.loading || isLoading}
                      ref={buttonRef}
                      style={{ marginBottom: "1rem" }}
                      type="primary"
                    >
                      Sign in
                    </Button>
                  )}
                  {state?.type !== "microsoft" && (
                    <Button
                      onClick={() => {
                        setPasswordResetStep("request")
                        setPasswordResetIdentifier("")
                        dispatch({
                          type: "field",
                          field: "reset",
                          payload: true,
                        })
                      }}
                      style={{ color: "white", textDecoration: "underline" }}
                      type="link"
                    >
                      Forgot your password?
                    </Button>
                  )}
                  {tenant?.providers
                    ?.filter((x) => x !== state?.type)
                    ?.map((provider) => (
                      <Button
                        block
                        disabled={state?.loading}
                        htmlType="button"
                        key={provider}
                        onClick={() =>
                          dispatch({
                            type: "field",
                            field: "type",
                            payload: provider,
                          })
                        }
                        style={{ color: "white", textDecoration: "underline" }}
                        type="link"
                      >
                        {tenant?.providerConfig?.[provider]?.title ||
                          "Sign in with " +
                            provider
                              .split(/(?=[A-Z])/)
                              .map((s) => s.toLowerCase())
                              .join(" ")}
                      </Button>
                    ))}
                  {tenant?.classroomEnabled && (
                    <Button
                      block
                      href={`/auth/classroom${state?.tenantId ? `?tenantId=${state?.tenantId}` : ""}`}
                      htmlType="button"
                      style={{ marginBottom: "1rem" }}
                      type="link"
                    >
                      Join a classroom
                    </Button>
                  )}
                  {tenant?.support?.length ? (
                    <Button
                      key="password-reset"
                      onClick={() =>
                        dispatch({
                          type: "field",
                          field: "support",
                          payload: true,
                        })
                      }
                      style={{ color: "white", textDecoration: "underline" }}
                      type="link"
                    >
                      Trouble signing in?
                    </Button>
                  ) : null}
                </>
              )}
            </Form>
          </Col>
        </Row>
      </div>
    </>
  )
}
